import {
  faFileCertificate,
  faFileExclamation,
  faFileInvoice,
  faFilePdf,
  faFileSignature,
  faLaptopFile,
  faMoneyBillWave,
  faPrescription,
  faPresentationScreen,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router-dom';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  List,
  ListSubheader,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  useTheme,
} from '@mui/material';
import { Allow } from '../../../../../components/auth/Allow';
import { useCowbellTranslations } from '../../../../../i18n/translations';
import type { WithChildrenProps } from '../../../../../types/components/common';
import usePlatformRegion from '../../../../../utils/hooks/usePlatformRegion';

const ListItem = (props: any) => {
  const theme = useTheme<any>();
  return (
    <Allow allow={props.allow ?? 'any'}>
      <MuiListItem
        button
        component={Link}
        {...props}
        style={{ color: theme.agencyDash.text.link }}
      >
        {props.children}
      </MuiListItem>
    </Allow>
  );
};

export const ListItemIcon = ({ children }: WithChildrenProps) => {
  const theme = useTheme<any>();
  return (
    <MuiListItemIcon style={{ color: theme.agencyDash.interactable }}>
      {children}
    </MuiListItemIcon>
  );
};

const ListItemText = ({ primary }: { primary: string }) => (
  <MuiListItemText primary={primary} data-qa={`MENU_ITEM:${primary}`} />
);

interface MenuOptionsProps {
  onClick: () => void;
}

export const YourAgencyMenuOptions = ({ onClick }: MenuOptionsProps) => {
  const { t } = useCowbellTranslations();
  return (
    <List
      dense
      subheader={
        <ListSubheader style={{ fontWeight: 'bold', fontSize: '1.33rem' }}>
          Your {t('agency')}
        </ListSubheader>
      }
    >
      <ListItem
        allow={['accounts:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_ACCOUNTS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faUser as IconProp} />
        </ListItemIcon>
        <ListItemText primary="Clients" />
      </ListItem>
      <ListItem
        allow={['accounts:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_SUBMISSIONS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faLaptopFile} />
        </ListItemIcon>
        <ListItemText primary="Submissions" />
      </ListItem>
      <ListItem
        allow={['inbox:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_QUOTES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faFileInvoice} />
        </ListItemIcon>
        <ListItemText primary="Quotes" />
      </ListItem>
      <ListItem
        allow={['inbox:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_BINDERS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faFileSignature} />
        </ListItemIcon>
        <ListItemText primary={t('binders')} />
      </ListItem>
      <ListItem
        allow={['policy:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_POLICIES}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faFileCertificate}
            style={{ position: 'relative', right: '0.3rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Policies" />
      </ListItem>
      <ListItem
        allow={['claims:view']}
        to={YourAgencyMenuOptionsLinks.AGENCY_CLAIMS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faFileExclamation} />
        </ListItemIcon>
        <ListItemText primary="Claims" />
      </ListItem>
      <ListItem
        allow={['commissions:view']}
        to={YourAgencyMenuOptionsLinks.COMMISSIONS}
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon
            size="lg"
            icon={faMoneyBillWave}
            style={{ position: 'relative', right: '0.2rem' }}
          />
        </ListItemIcon>
        <ListItemText primary="Commissions" />
      </ListItem>
    </List>
  );
};

const YourAgencyMenuOptionsLinks = {
  AGENCY_ACCOUNTS: '/agency/accounts',
  AGENCY_SUBMISSIONS: '/agency/submissions',
  AGENCY_QUOTES: '/agency/inbox',
  AGENCY_BINDERS: '/agency/binders',
  AGENCY_POLICIES: '/agency/policies',
  AGENCY_CLAIMS: '/agency/claims',
  COMMISSIONS: '/agency/manage/commissions',
};

export const ResourcesMenuOptions = ({ onClick }: MenuOptionsProps) => {
  const { isAU } = usePlatformRegion();

  return (
    <List
      dense
      subheader={
        <ListSubheader style={{ fontWeight: 'bold', fontSize: '1.33rem' }}>
          Resources
        </ListSubheader>
      }
    >
      <ListItem
        component="a"
        href={ResourcesMenuOptionsLinks.COWBELL_ACADEMY}
        target="_blank"
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faPresentationScreen} />
        </ListItemIcon>
        <ListItemText primary="Cowbell Academy" />
      </ListItem>
      <ListItem
        component="a"
        href={ResourcesMenuOptionsLinks.COWBELL_RX}
        target="_blank"
        onClick={onClick}
      >
        <ListItemIcon>
          <FontAwesomeIcon size="lg" icon={faPrescription} />
        </ListItemIcon>
        <ListItemText primary="Cowbell Rx" />
      </ListItem>
      {!isAU && (
        <ListItem
          to={ResourcesMenuOptionsLinks.FORMS_LIBRARY}
          onClick={onClick}
        >
          <ListItemIcon>
            <FontAwesomeIcon size="lg" icon={faFilePdf} />
          </ListItemIcon>
          <ListItemText primary="Forms Library" />
        </ListItem>
      )}
    </List>
  );
};

const ResourcesMenuOptionsLinks = {
  COWBELL_ACADEMY: 'https://academy.cowbell.insure',
  COWBELL_RX: 'https://cowbell.insure/rx',
  FORMS_LIBRARY: '/agency/manage/forms-library',
};
